/**
 * Wix Online Programs API – Headless integration.
 * Powers the Training page with core modules, advanced modules, quizzes,
 * assignments, simulations, and domain templates.
 *
 * Docs: https://dev.wix.com/docs/rest/api-reference/online-programs
 */
import { wixFetch } from "./client";

export interface OnlineProgram {
  id: string;
  name: string;
  description: string;
  level: "core" | "advanced";
  duration: string;
  modules: ProgramModule[];
  domain?: string;
}

export interface ProgramModule {
  id: string;
  title: string;
  type: "lesson" | "quiz" | "assignment" | "simulation";
  duration: string;
}

/** Fetch all online programs from Wix. */
export async function getOnlinePrograms(): Promise<OnlineProgram[]> {
  const res = await wixFetch<{ programs: OnlineProgram[] }>(
    "/online-programs/v1/programs",
    { method: "GET" },
  );
  return res?.programs ?? [];
}

/**
 * Seeded Sentinel training catalog used when Wix Online Programs is not
 * yet configured. Mirrors the structure returned by the live API.
 */
export const TRAINING_CATALOG: OnlineProgram[] = [
  {
    id: "core-fundamentals",
    name: "Sentinel Core Fundamentals",
    description:
      "Foundational training covering the four-layer architecture, sensor fusion, and the diagnostic engine.",
    level: "core",
    duration: "8 hours",
    modules: [
      { id: "m1", title: "Architecture Overview", type: "lesson", duration: "45 min" },
      { id: "m2", title: "Sensor Fusion Layer", type: "lesson", duration: "60 min" },
      { id: "m3", title: "Diagnostic Intelligence", type: "lesson", duration: "60 min" },
      { id: "m4", title: "Intervention Layer", type: "lesson", duration: "45 min" },
      { id: "m5", title: "Command Layer Operations", type: "lesson", duration: "45 min" },
      { id: "q1", title: "Fundamentals Quiz", type: "quiz", duration: "30 min" },
      { id: "a1", title: "Architecture Mapping Assignment", type: "assignment", duration: "90 min" },
    ],
  },
  {
    id: "core-domain-ops",
    name: "Domain Operations Essentials",
    description:
      "Operator-level training for running Sentinel within a specific safety domain.",
    level: "core",
    duration: "12 hours",
    modules: [
      { id: "m1", title: "Domain Template Selection", type: "lesson", duration: "40 min" },
      { id: "m2", title: "Diagnostic Profile Configuration", type: "lesson", duration: "50 min" },
      { id: "m3", title: "Intervention Template Activation", type: "lesson", duration: "50 min" },
      { id: "sim1", title: "Live Domain Simulation", type: "simulation", duration: "120 min" },
      { id: "q1", title: "Operations Quiz", type: "quiz", duration: "30 min" },
    ],
  },
  {
    id: "adv-diagnostic",
    name: "Advanced Diagnostic Engineering",
    description:
      "Deep dive into diagnostic profile engineering, root-cause analysis, and continuous learning loop tuning.",
    level: "advanced",
    duration: "16 hours",
    modules: [
      { id: "m1", title: "Diagnostic Profile Design", type: "lesson", duration: "90 min" },
      { id: "m2", title: "Root-Cause Analysis Methods", type: "lesson", duration: "75 min" },
      { id: "m3", title: "Learning Loop Tuning", type: "lesson", duration: "60 min" },
      { id: "sim1", title: "Multi-Domain Diagnostic Simulation", type: "simulation", duration: "180 min" },
      { id: "a1", title: "Profile Engineering Assignment", type: "assignment", duration: "120 min" },
      { id: "q1", title: "Advanced Diagnostics Exam", type: "quiz", duration: "45 min" },
    ],
  },
  {
    id: "adv-oem-integration",
    name: "OEM/Tier-1 Integration",
    description:
      "Advanced training for embedding Sentinel into OEM products and production lines using the integration SDK.",
    level: "advanced",
    duration: "20 hours",
    modules: [
      { id: "m1", title: "Integration SDK Overview", type: "lesson", duration: "60 min" },
      { id: "m2", title: "Sensor Bridge Implementation", type: "lesson", duration: "90 min" },
      { id: "m3", title: "Custom Intervention Libraries", type: "lesson", duration: "75 min" },
      { id: "sim1", title: "Full OEM Integration Simulation", type: "simulation", duration: "240 min" },
      { id: "a1", title: "Integration Architecture Assignment", type: "assignment", duration: "180 min" },
    ],
  },
];
